// Cuestionario que se llena ANTES de cada cotización (PLAN-NOCHE §2). Todo lo que pregunta sale de Diego.
import { z } from 'zod';
import { Correo, Tarifa, TipoProyecto, Texto, TARIFAS_CASA, TARIFAS_MEDIA_TENSION } from './basicos.ts';
import type { Etapa, FormatoFamilia, Tarifa as TipoTarifa } from './basicos.ts';
import { TARIFAS_QUE_PIDEN_12_RECIBOS } from '../constantes.ts';
import { cambioDeTarifa } from './tarifa-futura.ts';

/** Para quién es la cotización (TODO SE ELIGE DESDE EL INICIO, Diego 13-oct-2026, 22:45). En paneles es el tipo del proyecto. */
export const TIPOS_CLIENTE = ['casa', 'comercio', 'industria'] as const;
export type TipoCliente = (typeof TIPOS_CLIENTE)[number];

export const ClienteCuestionario = z.strictObject({
  /** Sale en el PDF (máximo 60 caracteres: lo exige el esquema v1 del generador). */
  nombre: Texto(2, 60),
  telefono: z.string().trim().regex(/^\d{10}$/, 'teléfono: 10 dígitos').nullable(),
  correo: Correo.nullable(),
  ciudad: Texto(2, 40),
  estado: Texto(2, 40),
  /** Giro del negocio (comercio e industria). */
  giro: Texto(2, 40).nullable(),
  /** Baterías o aislado: casa, comercio o industria, como se eligió en «Qué cotizas» (en paneles no va: lo dice el tipo del
   *  proyecto). Opcional: las cotizaciones de antes no lo traen y, al editarlas, se deduce del giro o la tarifa. Antes no se
   *  guardaba y una industria con baterías volvía al editar como comercio (verificación adversarial del 14-oct). */
  tipo: z.enum(TIPOS_CLIENTE).nullable().optional(),
});
export type ClienteCuestionario = z.infer<typeof ClienteCuestionario>;

/** El recibo puede estar a nombre de otra persona (el papá, el arrendador, la empresa). */
export const Titular = z.strictObject({
  es_el_cliente: z.boolean(),
  nombre: Texto(2, 120).nullable(),
});

/** Cliente nuevo sin recibos: cómo se estima su consumo (variante "consumo proyectado", ESPEC 4.2). */
export const Carga = z.strictObject({
  equipo: Texto(2, 60),
  cantidad: z.int().min(1).max(500),
  w: z.number().positive().max(1_000_000),
  horas_dia: z.number().min(0).max(24),
  meses_uso: z.int().min(1).max(12),
});
export type Carga = z.infer<typeof Carga>;

/** ADENDA z46-2 (P22, Diego 27-oct-2026): el cliente nuevo solo sabe cuánto PAGA a CFE (con IVA, como su recibo) y cada cuándo. */
export const PagoEstimacion = z.strictObject({
  monto: z.number().positive().max(10_000_000),
  periodo: z.enum(['bimestral', 'mensual']),
});
export type PagoEstimacion = z.infer<typeof PagoEstimacion>;

export const Estimacion = z.strictObject({
  /** 'pago' (z46-2): los kWh los saca el motor de lo que paga, con las cuotas de CFE de su tarifa (periodos.ts → kwhMesDePago). */
  metodo: z.enum(['cuadro_cargas', 'negocios_parecidos', 'kwh_mes', 'pago']),
  cargas: z.array(Carga).max(40),
  /** Si el vendedor ya trae un consumo mensual promedio estimado. */
  kwh_mes: z.number().positive().nullable(),
  /** z46-2: con metodo 'pago', lo que paga (ausente en las de antes). */
  pago: PagoEstimacion.nullable().optional(),
  fuente: Texto(2, 300),
});
export type Estimacion = z.infer<typeof Estimacion>;

/** Un inversor que el cliente YA tiene (variante "ampliacion" de la familia: marca, modelo, kW y cantidad). */
export const InversorExistente = z.strictObject({
  marca: Texto(2, 40),
  modelo: Texto(2, 40).nullable(),
  kw: z.number().positive().max(1_000),
  cantidad: z.int().min(1).max(50),
});
export type InversorExistente = z.infer<typeof InversorExistente>;

export const SistemaExistente = z.strictObject({
  paneles: z.int().min(1).max(10_000).nullable(),
  panel_w: z.number().positive().max(1_000).nullable(),
  kwp: z.number().positive().max(100_000).nullable(),
  /** LEGADO (texto libre de antes de la variante "ampliacion"): el PDF ya no lo usa; va en `inversores`. */
  inversor: Texto(2, 120).nullable(),
  notas: z.string().trim().max(1_000).nullable(),
  // Etapa "Conectar" (10-oct-2026): lo que pide la variante "ampliacion" de la familia. Opcionales para que los
  // borradores de antes sigan validando; null = no se sabe (en una propuesta FINAL el PDF exige inversores).
  /** Sus inversores, estructurados (el PDF los imprime y, con `conexion: inversor_actual`, cada opción los repite). */
  inversores: z.array(InversorExistente).min(1).max(10).nullable().optional(),
  /** Año en que se instaló (no puede ser después de la fecha de la propuesta). */
  anio_instalacion: z.int().min(1990).max(2100).nullable().optional(),
  /** Quién lo instaló. */
  instalo: z.enum(['solarix', 'otro']).nullable().optional(),
  /** ¿Los paneles nuevos llevan su propio inversor o caben en el que ya tiene? null/ausente = inversor nuevo. */
  conexion: z.enum(['inversor_nuevo', 'inversor_actual']).nullable().optional(),
});
export type SistemaExistente = z.infer<typeof SistemaExistente>;

/** Incentivo fiscal (Diego, 12-oct, MANDA; LISR art. 34, fr. XIII: deducción del 100 % si el equipo opera ≥ 5 años). Solo
 *  persona moral y persona física con actividad empresarial deducen: la propuesta les muestra el beneficio fiscal estimado
 *  (contado sin IVA × tasa de ISR) y un segundo retorno "considerando la deducción fiscal"; particular, nada de esto. */
/** Revisión de ventas, propuesta 5 (Diego, 12-oct noche): el régimen es OBLIGATORIO en comercio e industria, con la opción
 *  «No sé todavía» (no_se): así la persona moral y la persona física con actividad empresarial siempre reciben su recuadro con
 *  el beneficio y el segundo retorno (antes dependía de que el vendedor se acordara de llenarlo). Con «No sé todavía» la
 *  propuesta dice la deducción sin cifras, como un cuestionario de antes sin régimen. */
export const REGIMENES_FISCALES = ['persona_moral', 'pf_empresarial', 'pf_resico', 'particular', 'no_se'] as const;
export type RegimenFiscal = (typeof REGIMENES_FISCALES)[number];
export const ETIQUETA_REGIMEN_FISCAL: Record<RegimenFiscal, string> = {
  persona_moral: 'Persona moral',
  pf_empresarial: 'Persona física con actividad empresarial',
  // Etapa 2 (12-oct): la persona física en RESICO NO puede deducir (LISR art. 113-E: su ISR se calcula "sin aplicar
  // deducción alguna"; Cotizador/investigacion/REGULACION-GD-2026-10.md §5). Se trata como particular. La persona MORAL en
  // RESICO sí deduce (art. 209-B, fr. XIII): va como "Persona moral".
  pf_resico: 'Persona física en RESICO (no deduce)',
  particular: 'Particular (no deduce)',
  no_se: 'No sé todavía (la deducción va sin cifras)',
};
/** Los regímenes que NO deducen el sistema: el particular y la persona física en RESICO (LISR art. 113-E). La propuesta no
 *  les habla de deducción (ni el texto de la ley ni cifras). «No sé todavía» NO es uno de ellos: la deducción se ofrece, sin
 *  cifras. */
export const REGIMENES_SIN_DEDUCCION: readonly RegimenFiscal[] = Object.freeze(['particular', 'pf_resico']);
/** ¿Este régimen deduce? (persona moral o persona física con actividad empresarial). Lista POSITIVA: un régimen nuevo
 *  (como «No sé todavía») no deduce con cifras hasta que se diga aquí. */
export function regimenDeduce(r: RegimenFiscal): r is 'persona_moral' | 'pf_empresarial' {
  return r === 'persona_moral' || r === 'pf_empresarial';
}
/** ¿El régimen dice que NO deduce? (particular o persona física en RESICO; «No sé todavía», no). */
export function regimenNoDeduce(r: RegimenFiscal): boolean {
  return REGIMENES_SIN_DEDUCCION.includes(r);
}
/** FINSUS tiene DOS productos (Diego, 16-oct-2026; corrida real de FINSUS del 16-oct): «empresarial» = CRÉDITO SOLAR
 *  EMPRESARIAL (28-oct-2026: tasa 15.99 % anual sin IVA en intereses, seguro de daños de contado, sin seguro de vida, comisión por
 *  apertura PAGADA DE CONTADO, sin CAT) para la PERSONA MORAL y la PERSONA FÍSICA CON ACTIVIDAD EMPRESARIAL (Diego,
 *  16-oct), y la PERSONA FÍSICA EN RESICO (z27-3, 22-oct); particular, sin régimen o «No sé todavía» siguen con «persona». UNA sola regla para
 *  el motor, el servidor simulado y el PDF (la plantilla elige igual con cliente.regimen: lib/nucleo/finanzas.mjs →
 *  productoFinsus; si no coinciden, el cruce del FINSUS impreso no entrega el PDF). */
export const PRODUCTOS_FINSUS = ['persona', 'empresarial'] as const;
export type ProductoFinsus = (typeof PRODUCTOS_FINSUS)[number];
export function productoFinsus(c: ClienteDeNegocio & Pick<Cuestionario, 'fiscal'>): ProductoFinsus {
  // 20-oct-2026 (z21: «solo particulares y casas van con el de persona»): una casa que llega por API con régimen de empresa
  // salía con el empresarial. El régimen solo cuenta en un negocio (esNegocio).
  // ADENDA z27-3 (Diego, 22-oct-2026): RESICO también va con el EMPRESARIAL (es persona física con actividad empresarial).
  const r = c.fiscal?.regimen;
  return esNegocio(c) && (r === 'persona_moral' || r === 'pf_empresarial' || r === 'pf_resico') ? 'empresarial' : 'persona';
}
/** ¿Al cuestionario le falta el régimen que exige comercio e industria? (revisión de ventas, propuesta 5). Las cotizaciones
 *  de antes sin régimen siguen abriendo, con un aviso amarillo del motor; una nueva no se crea sin él (api: CuerpoCrearCotizacion)
 *  y una edición no se lo puede quitar (servidor). */
export function faltaRegimen(c: ClienteDeNegocio & Pick<Cuestionario, 'fiscal'>): boolean {
  return esNegocio(c) && !c.fiscal;
}
/** Lo que hace falta para saber si el cliente es un negocio (esNegocio). */
export type ClienteDeNegocio = Pick<Cuestionario, 'tipo'> & { cliente?: { tipo?: TipoCliente | null } | null; tarifa?: Cuestionario['tarifa'] };
/** ¿El cliente es un NEGOCIO (comercio o industria)? En paneles lo dice el tipo del proyecto; en baterías o aislado, el tipo de
 *  cliente elegido en «Qué cotizas» (cliente.tipo). Las de baterías de antes sin cliente.tipo: negocio si su tarifa no es de
 *  casa (como el PDF de baterías). 20-oct-2026: baterías de un negocio no preguntaban el régimen y salían con el FINSUS de
 *  persona; z21 «solo particulares y casas van con el de persona». */
export function esNegocio(c: ClienteDeNegocio): boolean {
  if (c.tipo === 'comercio' || c.tipo === 'industria') return true;
  if (c.tipo !== 'baterias' && c.tipo !== 'aislado') return false;
  const t = c.cliente?.tipo;
  if (t) return t === 'comercio' || t === 'industria';
  return !!c.tarifa && c.tarifa !== 'no_se' && !(TARIFAS_CASA as readonly string[]).includes(c.tarifa);
}
export const MENSAJE_FALTA_REGIMEN = 'Negocio: elige el régimen fiscal del cliente (o «No sé todavía»). Con persona moral o actividad empresarial, la propuesta le enseña su beneficio fiscal y en cuánto se paga con la deducción.';
/** ADENDA z44/z45 (Diego, 27-oct-2026): «el aislado es solo para residencial». ¿Es un aislado de un NEGOCIO (comercio o
 *  industria, como lo decide esNegocio)? Uno NUEVO ya no se crea (api: CuerpoCrearCotizacion; servidor al crear) ni una edición
 *  vuelve aislado de negocio lo que no lo era; el que ya estaba guardado se sigue abriendo, editando y sacando PDF, con un
 *  amarillo del motor. */
export function aisladoDeNegocio(c: ClienteDeNegocio): boolean {
  return c.tipo === 'aislado' && esNegocio(c);
}
export const MENSAJE_AISLADO_SOLO_CASA = 'El sistema aislado (sin CFE) es solo para casas: a un comercio o una industria cotízale paneles, o paneles con baterías.';
/** ISR por omisión: 30 % personas morales; persona física con actividad empresarial, la suya (por omisión 30 %, editable). */
export const TASA_ISR_POR_OMISION = 30;
export const FiscalCuestionario = z.strictObject({
  regimen: z.enum(REGIMENES_FISCALES),
  tasa_isr_pct: z.number().min(1).max(40),
});
export type FiscalCuestionario = z.infer<typeof FiscalCuestionario>;
/** ¿El cliente deduce el sistema? (persona moral o física con actividad empresarial, y solo en comercio o industria: el
 *  formato residencial no lleva deducción). */
export function deduceImpuestos(c: Pick<Cuestionario, 'tipo' | 'fiscal'>): boolean {
  return (c.tipo === 'comercio' || c.tipo === 'industria') && !!c.fiscal && regimenDeduce(c.fiscal.regimen);
}
/** ¿El cuestionario dice que el cliente NO deduce? (particular o persona física en RESICO). Sin régimen capturado o con «No sé
 *  todavía»: false (la propuesta de negocio dice la deducción sin cifras). */
export function noDeduce(c: Pick<Cuestionario, 'fiscal'>): boolean {
  return !!c.fiscal && regimenNoDeduce(c.fiscal.regimen);
}
/** ¿Se OFRECE la deducción (parametros.formas_de_pago.deduccion) con este cuestionario? Negocio (comercio o industria) que no
 *  dice "no deduzco" (sin régimen capturado, como antes: sí). Es la regla de los parámetros iniciales del motor y la que usa el
 *  servidor para sincronizar el interruptor cuando el cuestionario cambia de régimen o de tipo (verificación adversarial de la
 *  etapa 2: corregir "Particular" o "RESICO" a "Persona moral" dejaba la deducción apagada sin ningún aviso). */
export function ofreceDeduccion(c: Pick<Cuestionario, 'tipo' | 'fiscal'>): boolean {
  return (c.tipo === 'comercio' || c.tipo === 'industria') && !noDeduce(c);
}

/** Diego, 12-oct (punto 4 + respuestas del mediodía, MANDAN): tipo de estructura de la obra. Cambia el COSTO por panel
 *  (estructura y mano de obra, editables por Diego en Configuración → Tabla de precios) y, para cuidar el margen, el
 *  precio del sistema sube con lo que cuesta de más contra la losa/lámina. Ausente = losa/lámina (cotizaciones de antes:
 *  no truenan al abrirse ni al recalcular). */
export const TIPOS_ESTRUCTURA = ['losa_lamina', 'ptr', 'suelo', 'carport'] as const;
export type TipoEstructura = (typeof TIPOS_ESTRUCTURA)[number];
export const ETIQUETA_ESTRUCTURA: Record<TipoEstructura, string> = {
  losa_lamina: 'Losa o lámina',
  ptr: 'PTR (estructura elevada de acero)',
  suelo: 'Sobre suelo',
  carport: 'Carport (cochera)',
};
export const DETALLE_ESTRUCTURA: Record<TipoEstructura, string> = {
  losa_lamina: 'Aluminio sobre el techo (la de siempre).',
  ptr: 'Base elevada de acero sobre el techo.',
  suelo: 'Estructura sobre el terreno.',
  carport: 'Techo de cochera con los paneles encima.',
};
/** Diego, 12-oct tarde (ADENDA, MANDA): "sobre suelo y carport nunca es para residencial, solo comercial e industrial". En casa
 *  el cuestionario ofrece solo losa/lámina y PTR; una casa que ya traía suelo o carport sigue valiendo (no truena al abrirse) y
 *  el motor lo avisa en amarillo. */
export function estructuraSoloNegocio(t: TipoEstructura): boolean {
  return t === 'suelo' || t === 'carport';
}
/** Nombre corto para meter en una frase ("Estructura PTR: …", "el cuestionario dice carport (cochera)"): nunca la etiqueta
 *  completa en minúsculas ("estructura ptr (estructura elevada de acero)"). */
export const NOMBRE_CORTO_ESTRUCTURA: Record<TipoEstructura, string> = {
  losa_lamina: 'losa o lámina',
  ptr: 'PTR',
  suelo: 'sobre suelo',
  carport: 'carport (cochera)',
};
/** La estructura de la cotización (ausente = losa/lámina). Baterías SIN paneles (respaldo con `con_paneles` false) no llevan
 *  estructura: aunque la forma la haya dejado puesta (el selector se oculta), cuenta como losa/lámina (no cambia el precio,
 *  no avisa "por confirmar" ni va al PDF). */
export function estructuraDe(c: { estructura?: TipoEstructura | null | undefined; respaldo?: { con_paneles: boolean } | null | undefined }): TipoEstructura {
  if (c.respaldo && c.respaldo.con_paneles === false) return 'losa_lamina';
  return c.estructura ?? 'losa_lamina';
}

/** Diego, 12-oct (punto 6, MANDA): fases y tensión del servicio de CFE, que deciden qué inversores Growatt caben. Tensiones
 *  que de verdad entrega CFE (NMX-J-098-ANCE, tensiones normalizadas de baja tensión): 127 V entre fase y neutro
 *  (monofásico de 2 hilos); 220Y/127 V (bifásico de 3 hilos o trifásico de 4 hilos, 220 V entre fases); 240/120 V
 *  (monofásico de 3 hilos, de transformador monofásico: 240 V entre fases). En media tensión (GDMTO/GDMTH) CFE entrega en
 *  13.2 o 33 kV y lo que importa es el SECUNDARIO del transformador del cliente: 220Y/127, 440 o 480Y/277 V. Ausente =
 *  no se preguntó: el motor lo deduce como antes (tarifa, hilos del recibo, tamaño) y pide confirmarlo en la visita. */
export const FASES_SERVICIO = ['monofasico', 'bifasico', 'trifasico'] as const;
export type FasesServicio = (typeof FASES_SERVICIO)[number];
export const TENSIONES_SERVICIO = ['127', '220', '240', '440', '480'] as const;
export type TensionServicio = (typeof TENSIONES_SERVICIO)[number];
/** Las tensiones que existen con cada número de fases (las demás combinaciones no las entrega CFE). */
export const TENSIONES_POR_FASES: Readonly<Record<FasesServicio, readonly TensionServicio[]>> = Object.freeze({
  monofasico: ['127'],
  bifasico: ['220', '240'],
  trifasico: ['220', '440', '480'],
});
/** Diego, 24-oct-2026 (MANDA): en la pantalla solo bifásico o trifásico. «Monofásico» no se ofrece: los inversores van a 220 V
 *  entre dos fases y el servicio se instala bifásico (2 o 3 hilos en el recibo = bifásico). Un cuestionario de antes con
 *  «monofasico» se sigue abriendo y calculando (el esquema lo acepta y la pantalla lo enseña mientras sea su valor). */
export const FASES_EN_PANTALLA: readonly FasesServicio[] = Object.freeze(['bifasico', 'trifasico']);
/** Diego, 24-oct-2026 (MANDA): las tensiones normales son 220 o 440 V (deciden el tipo de inversor). 240, 480 y 127 V ya no se
 *  ofrecen; si un cuestionario de antes las trae, se respetan. */
export const TENSIONES_EN_PANTALLA: readonly TensionServicio[] = Object.freeze(['220', '440']);
export const ETIQUETA_FASES: Record<FasesServicio, string> = {
  monofasico: 'Monofásico (2 hilos: una fase y neutro)',
  bifasico: 'Bifásico (2 o 3 hilos en el recibo)',
  trifasico: 'Trifásico (4 hilos, o media tensión con transformador)',
};
export const ETIQUETA_TENSION: Record<TensionServicio, string> = {
  '127': '127 V (fase y neutro)',
  '220': '220 V entre fases (127 V a neutro)',
  '240': '240 V entre fases (120 V a neutro)',
  '440': '440 V (transformador de la planta)',
  // corta: en el celular (390 px) el selector corta a media palabra lo que pase de ~250 px; "transformador de la planta" ya
  // lo dice la ayuda del campo
  '480': '480 V (480/277 V)',
};
/** 440 o 480 V solo existen como secundario del transformador propio del cliente (media tensión: GDMTO/GDMTH). En baja
 *  tensión (casa, PDBT, GDBT) CFE entrega 220Y/127 o 240/120 V: ahí 440/480 casi seguro es un error de captura. */
export function tensionDeTransformador(t: TensionServicio | null | undefined): boolean {
  return t === '440' || t === '480';
}
export const ServicioCuestionario = z.strictObject({
  fases: z.enum(FASES_SERVICIO),
  /** null = sabe las fases pero no la tensión (se toma la más común de esas fases y se avisa). */
  tension: z.enum(TENSIONES_SERVICIO).nullable(),
});
export type ServicioCuestionario = z.infer<typeof ServicioCuestionario>;

/** Diego, 12-oct tarde (ADENDA, MANDA): en media tensión (GDMTO/GDMTH) la tensión del TRANSFORMADOR es PREGUNTA OBLIGATORIA
 *  del cuestionario: 220 o 440 V (Diego, 24-oct: «normalmente es 220 o 440»; antes 220 o 480), o «No sé: lo veo en la visita».
 *  Esa respuesta se guarda como servicio trifásico SIN tensión (el motor calcula con 220 V y deja un aviso amarillo). 480 V
 *  ya no se ofrece; si un cuestionario de antes lo trae, se respeta (440 y 480 V dan los mismos inversores). La pantalla la exige; el esquema NO (las cotizaciones de antes, sin la pregunta, siguen
 *  abriéndose y recalculándose; el motor les deja el aviso amarillo de que falta). */
export const TENSIONES_TRANSFORMADOR: readonly TensionServicio[] = Object.freeze(['220', '440']);
export const ETIQUETA_NO_SE_TRANSFORMADOR = 'No sé: lo veo en la visita';
/** ¿Falta contestar la tensión del transformador? (tarifa de media tensión y el servicio no es trifásico: sin contestar, o
 *  una respuesta de baja tensión que no aplica). */
export function faltaTensionTransformador(c: { tarifa: TipoTarifa | 'no_se' | null; servicio?: ServicioCuestionario | null | undefined }): boolean {
  return esMediaTension(c.tarifa) && c.servicio?.fases !== 'trifasico';
}

/** La noche más larga del año en Sonora (sin sol para los paneles), en horas: tope de las horas de noche de un equipo. Con
 *  ella, el día más corto tiene unas 24 − 14 = 10 h de sol. */
export const HORAS_NOCHE_MAX = 14;
/** La noche más CORTA del año en Sonora (verano: unas 14 h de sol), en horas. Un equipo que se usa más de 14 h al día trabaja
 *  de noche, por lo menos, lo que pasa de esas 14 (el esquema no acepta menos: verificación adversarial del 14-oct). */
export const HORAS_NOCHE_MIN = 10;

/** Las horas de NOCHE de un equipo cuando el vendedor no las escribió (verificación adversarial del 14-oct): las típicas del
 *  equipo sin pasar de las horas al día, pero nunca menos de lo que pasa de las horas de sol del día más corto (10 h en
 *  Sonora): una bomba a 24 h trabaja 14 h de noche, no las 0 de una bomba típica de 2 h. La usan el motor (usoDeCarga) y la
 *  pantalla (lo que se ve en gris en «De noche»): así dicen lo mismo. */
export function horasNocheDeOmision(tipicaNoche: number, horasDia: number): number {
  return Math.max(Math.min(tipicaNoche, horasDia), horasDia - (24 - HORAS_NOCHE_MAX));
}

/** AISLADO (Diego, 13-oct 23:00, respuesta w): horas de uso al día, de noche, y el ARRANQUE (veces su potencia al encender)
 *  de cada equipo POR OMISIÓN, por su nombre (al inicio de una palabra, sin acentos; gana la primera fila que coincide).
 *  SUPUESTO del cotizador (14-oct), POR CONFIRMAR CON DIEGO: valores típicos con la potencia de placa (un refrigerador de
 *  200 W no trabaja las 24 h: su compresor cicla; los motores arrancan con 3 veces su potencia). El vendedor los cambia en el
 *  cuestionario. Los usan el motor (banco de la noche, pico del inversor) y la pantalla (lo que se ve por omisión). */
export const USO_EQUIPO: readonly { palabras: readonly string[]; horas_dia: number; horas_noche: number; arranque: number }[] = Object.freeze([
  { palabras: ['refri', 'congelador', 'nevera', 'vitrina'], horas_dia: 10, horas_noche: 5, arranque: 3 },
  { palabras: ['minisplit', 'mini split', 'aire', 'clima', 'a/c', 'cooler', 'enfriador'], horas_dia: 8, horas_noche: 6, arranque: 3 },
  { palabras: ['foco', 'luz', 'luces', 'lampara', 'ilumin', 'reflector'], horas_dia: 6, horas_noche: 5, arranque: 1 },
  // prendido las 24 h: toda la noche, y la más larga del año tiene 14 h (verificación adversarial del 14-oct: antes 12)
  { palabras: ['modem', 'internet', 'router', 'wifi', 'wi-fi', 'camara', 'cctv', 'alarma', 'dvr'], horas_dia: 24, horas_noche: 14, arranque: 1 },
  { palabras: ['ventilador', 'abanico'], horas_dia: 10, horas_noche: 8, arranque: 1 },
  { palabras: ['cpap', 'oxigeno', 'medico', 'concentrador'], horas_dia: 8, horas_noche: 8, arranque: 1 },
  { palabras: ['tele', 'tv', 'pantalla', 'television'], horas_dia: 5, horas_noche: 3, arranque: 1 },
  { palabras: ['compu', 'pc', 'laptop', 'monitor', 'impresora'], horas_dia: 6, horas_noche: 2, arranque: 1 },
  { palabras: ['celular', 'cargador', 'telefono', 'radio'], horas_dia: 3, horas_noche: 2, arranque: 1 },
  { palabras: ['bomba', 'presurizador', 'hidro', 'pozo'], horas_dia: 2, horas_noche: 0, arranque: 3 },
  { palabras: ['lavadora', 'compresor', 'soldadora', 'taladro', 'herramienta', 'porton'], horas_dia: 1, horas_noche: 0, arranque: 3 },
  { palabras: ['microondas', 'horno', 'estufa', 'parrilla', 'plancha', 'secadora', 'cafetera', 'licuadora'], horas_dia: 0.5, horas_noche: 0.25, arranque: 1 },
  { palabras: ['boiler', 'calentador'], horas_dia: 2, horas_noche: 1, arranque: 1 },
]);
/** Lo que no coincide con ninguna fila: 4 h al día, 2 de noche, sin arranque. */
export const USO_EQUIPO_OTRO = Object.freeze({ horas_dia: 4, horas_noche: 2, arranque: 1 });

const sinAcentosMinus = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
/** Las horas de uso y el arranque de un equipo por su nombre (USO_EQUIPO), sin lo que escribió el vendedor. */
export function usoPorOmision(equipo: string): { horas_dia: number; horas_noche: number; arranque: number } {
  const n = sinAcentosMinus(equipo);
  const toca = (p: string) => new RegExp(`(^|[^a-z0-9])${sinAcentosMinus(p).replace(/[.*+?^${}()|[\]\\/]/g, '\\$&')}`).test(n);
  const f = USO_EQUIPO.find((x) => x.palabras.some(toca));
  return f ? { horas_dia: f.horas_dia, horas_noche: f.horas_noche, arranque: f.arranque } : { ...USO_EQUIPO_OTRO };
}
/** Las horas con que se calcula un equipo: las que escribió el vendedor o, si no, las de omisión. Si solo escribió las horas
 *  al día, las de noche salen de ellas (horasNocheDeOmision: las típicas sin pasar del día, y nunca menos de lo que pasa de
 *  las 10 h de sol del día más corto). */
export function usoDeCarga(c: { equipo: string; horas_dia?: number | null; horas_noche?: number | null }): { horas_dia: number; horas_noche: number; arranque: number; de_omision: boolean } {
  const o = usoPorOmision(c.equipo);
  const dia = c.horas_dia ?? o.horas_dia;
  const noche = c.horas_noche ?? horasNocheDeOmision(o.horas_noche, dia);
  return { horas_dia: dia, horas_noche: noche, arranque: o.arranque, de_omision: c.horas_dia == null || c.horas_noche == null };
}

/** Contrato v2 · baterías (ESPEC 3.4): un equipo que debe seguir funcionando cuando se va la luz. AISLADO (Diego, 13-oct
 *  23:00, respuesta w: «el banco se dimensiona por omisión para solo la noche»): sus horas de uso al día y cuántas de ellas son
 *  de noche (sin sol). Opcionales: ausente/null = las de omisión del equipo (usoPorOmision, editables en el cuestionario). */
export const CargaRespaldo = z
  .strictObject({
    equipo: Texto(2, 60),
    cantidad: z.int().min(1).max(500),
    w: z.number().positive().max(100_000),
    /** Horas de uso al día (de día y de noche), con su potencia de placa. */
    horas_dia: z.number().positive().max(24).nullable().optional(),
    /** De esas horas, cuántas son de noche (sin sol): con ellas se dimensiona el banco del aislado. */
    horas_noche: z.number().min(0).max(HORAS_NOCHE_MAX).nullable().optional(),
  })
  .superRefine((c, ctx) => {
    const u = usoDeCarga(c);
    if (u.horas_noche > u.horas_dia + 1e-9)
      ctx.addIssue({ code: 'custom', path: ['horas_noche'], message: `${c.equipo}: las horas de noche (${u.horas_noche}) no pueden ser más que las horas de uso al día (${u.horas_dia}).` });
    // Verificación adversarial del 14-oct: 24 h al día con 0 de noche no existe. El día más largo en Sonora tiene unas 14 h de
    // sol: lo que pasa de ellas es de noche (y el banco del aislado se calcula con eso).
    const minima = Math.round((u.horas_dia - (24 - HORAS_NOCHE_MIN)) * 100) / 100;
    if (minima > 0 && u.horas_noche < minima - 1e-9)
      ctx.addIssue({ code: 'custom', path: ['horas_noche'], message: `${c.equipo}: con ${u.horas_dia} horas al día, al menos ${minima} son de noche (el día más largo en Sonora tiene unas ${24 - HORAS_NOCHE_MIN} h de sol). Sube las horas de noche o baja las del día.` });
  });
export type CargaRespaldo = z.infer<typeof CargaRespaldo>;

/** Contrato v2 · lo que el cliente quiere respaldar (tipo "baterias" o "aislado"). Decisión de Diego del 11-oct (MANDA):
 *  el respaldo y el híbrido conectan TODA la casa al centro de carga principal (sin cargas críticas ni tablero): el motor
 *  arma los niveles por HORAS (~2, 4 y 8 h) con el consumo del recibo, así que ahí `cargas` va vacía. Solo el aislado
 *  (casi nunca) sigue por cargas y horas de uso, y ahí es obligatoria. */
export const RespaldoCuestionario = z.strictObject({
  /** respaldo = baterías para apagones; hibrido = con paneles y red; aislado = sin red de CFE. */
  tipo: z.enum(['respaldo', 'hibrido', 'aislado']),
  con_paneles: z.boolean(),
  cargas: z.array(CargaRespaldo).max(40),
  /** "Que aguante toda la noche", "solo el refri y el internet"… lo lee la IA. */
  notas: z.string().trim().max(500).nullable(),
});
export type RespaldoCuestionario = z.infer<typeof RespaldoCuestionario>;

/** ADENDA z52-2 (Diego, 28-oct-2026; caso SX-2026-0031): «si te ponen que va a aumentar el consumo… tú hagas una sugerencia con
 *  IA de cuánto aumentaría con los equipos adicionales y la descripción». Lo que el vendedor ACEPTÓ (un clic) de la sugerencia: los
 *  kWh DE MÁS por mes calendario (la única fuente del motor: periodos.ts los suma a cada periodo y recalcula los pesos) y, solo
 *  para explicar, los equipos nuevos y con qué descripción se calculó. Solo con recibo, en casa, comercio o industria. */
export const MOTIVO_AUMENTO_MAX = 40;
/** Bloqueo del verificador (29-oct; z55(4), lo conservador): lo que DECIDIÓ la pantalla al guardar el aumento, con los datos que
 *  tenía. El motor lo compara con la tarifa con que de verdad calcula (motor.ts → candado del aumento): con los MISMOS datos
 *  (recibo, tarifa del cuestionario y `negocio_a_pdbt`) tienen que coincidir o truena (INTERNO; p. ej. una pantalla vieja en caché
 *  que decide con otra regla). Con datos distintos (el recibo llegó o se volvió a leer después) es una decisión vieja: si la tarifa
 *  nueva no suma el aumento, el motor lo dice en amarillo y pide el consumo futuro (periodos.ts). Sin valor por omisión: un aumento
 *  sin decisión no pasa el esquema. */
export const DecisionAumento = z.strictObject({
  /** La tarifa que leyó la IA del recibo cuando se guardó (null = todavía sin recibo). */
  tarifa_leida: Tarifa.nullable(),
  /** La tarifa del cuestionario cuando se guardó. */
  tarifa_cuestionario: z.union([Tarifa, z.literal('no_se')]),
  /** El parámetro `negocio_a_pdbt` con que decidió la pantalla (en una cotización nueva, el que le pone parametrosIniciales). */
  negocio_a_pdbt: z.boolean(),
  /** Con qué tarifa calcularía el motor según la pantalla (tarifaDeCalculoConRecibo). null = SUPUESTA (cuestionario «No sé» y sin
   *  recibo): la pantalla enseñó «Consumo futuro al año» y la IA solo con su botón (modoAumento = 'supuesta'). */
  tarifa_calculo: Tarifa.nullable(),
});
export type DecisionAumento = z.infer<typeof DecisionAumento>;
export const AumentoConsumo = z.strictObject({
  /** kWh DE MÁS por mes calendario, ENE…DIC (≥ 0). */
  kwh_mes: z.array(z.number().nonnegative().max(1_000_000)).length(12),
  /** Los equipos NUEVOS que propuso la IA o las reglas (solo para explicar). */
  cargas: z.array(Carga).max(40),
  /** El «¿Por qué?» con que se calculó (si cambia, la pantalla pide volver a calcular). */
  descripcion: z.string().trim().max(500),
  fuente: z.enum(['ia', 'ia_simulada', 'reglas', 'vendedor']),
  /** Cómo lo dice la propuesta: «aires de 5 ton» (cabe en la nota de la portada). */
  motivo: z.string().trim().min(3).max(MOTIVO_AUMENTO_MAX),
  /** Lo que decidió la pantalla al guardarlo (DecisionAumento): el motor lo compara con lo que de verdad calcula. */
  pantalla: DecisionAumento,
});
export type AumentoConsumo = z.infer<typeof AumentoConsumo>;
/** Lo que acepta el vendedor con «Usar este aumento» (la decisión de la pantalla se le pone al guardar: borradorCuestionario). */
export type AumentoAceptado = Omit<AumentoConsumo, 'pantalla'>;

/** ADENDA z52-2: lo que pide el CUESTIONARIO para el aumento con IA (el esquema lo exige igual): va a subir, con recibo, en casa,
 *  comercio o industria, sin ampliación ni dos medidores. Falta la tarifa: ver aumentoConIAAplica. */
export function aumentoConIAPorCuestionario(c: Pick<Cuestionario, 'tipo' | 'consumo' | 'ampliacion' | 'dos_medidores'>): boolean {
  return c.consumo.cambio === 'aumenta' && !c.consumo.es_nuevo && !c.ampliacion.es_ampliacion && !c.dos_medidores && (c.tipo === 'casa' || c.tipo === 'comercio' || c.tipo === 'industria');
}

/** Bloqueo del verificador de regresiones (28-oct): las tarifas donde el motor NO suma el aumento con IA. Con cargos de demanda
 *  (GDBT, GDMTO, GDMTH) al subir el consumo también sube la demanda y el cotizador todavía no la estima: ahí manda lo de
 *  producción («Consumo futuro al año», la Holgada recomendada y la variante aumento_consumo). periodos.ts lee esta MISMA lista. */
export const TARIFAS_SIN_AUMENTO_IA: readonly TipoTarifa[] = Object.freeze(['GDBT', 'GDMTO', 'GDMTH']);
export function aumentoSeSumaEn(tarifa: TipoTarifa): boolean {
  return !TARIFAS_SIN_AUMENTO_IA.includes(tarifa);
}

/** ADENDA z52-2 + bloqueo del verificador (28-oct): ¿se ofrece «Calcular el aumento» con IA? Solo donde el motor lo SUMA de verdad:
 *  lo del cuestionario (aumentoConIAPorCuestionario) y que la tarifa con que calcularía el motor no tenga cargos de demanda. La
 *  tarifa es la de periodos.ts (tarifaDe + cambioDeTarifa): la del recibo (un negocio con recibo de casa y el parámetro
 *  `negocio_a_pdbt`, la futura: PDBT, o GDBT si se escogió), si no la del cuestionario; si nadie la sabe, la que supone el motor
 *  (casa: la de casa de su ciudad, industria: GDMTO, comercio: PDBT). La pantalla (cuestionario), las sugerencias de notas, el
 *  amarillo del motor y el PDF dicen lo MISMO; el motor además lo compara con la tarifa con que de verdad calculó y truena si no
 *  coincide. Los dos datos de la tarifa son OBLIGATORIOS (sin valor por omisión: un sitio sin migrar no compila). */
export function aumentoConIAAplica(
  c: Pick<Cuestionario, 'tipo' | 'consumo' | 'ampliacion' | 'dos_medidores' | 'tarifa'>,
  t: { tarifaLeida: TipoTarifa | null; negocioAPdbt: boolean },
): boolean {
  return modoAumento(c, t) !== 'produccion';
}

/** Bloqueo del verificador (29-oct; z55(4), lo conservador): CÓMO ofrece la pantalla «va a subir».
 *  · 'ia' (el diseño de z52-2): la tarifa con que calcula el motor se SABE (leída del recibo, o escrita en el cuestionario) y suma
 *    el aumento: «Calcular el aumento» con IA reemplaza «Consumo futuro al año» y calcula solo al salir de «¿Por qué?».
 *  · 'supuesta': nadie la sabe (cuestionario «No sé» y sin recibo, p. ej. un comercio al CREAR: el recibo se sube después). Si el
 *    recibo resulta GDBT, GDMTO o GDMTH el aumento no se suma: mientras, la pantalla enseña «Consumo futuro al año» (producción) y
 *    la IA solo con su BOTÓN (nunca se llama sola). En cuanto el recibo diga casa, PDBT o 1F de negocio (base futura), pasa a 'ia'.
 *  · 'produccion': lo de producción («Consumo futuro al año», sin IA): GDBT, GDMTO, GDMTH, la industria sin tarifa (el motor
 *    supone GDMTO) y lo que no cumple el cuestionario (aumentoConIAPorCuestionario). */
export type ModoAumento = 'ia' | 'supuesta' | 'produccion';
export function modoAumento(
  c: Pick<Cuestionario, 'tipo' | 'consumo' | 'ampliacion' | 'dos_medidores' | 'tarifa'>,
  t: { tarifaLeida: TipoTarifa | null; negocioAPdbt: boolean },
): ModoAumento {
  if (!aumentoConIAPorCuestionario(c)) return 'produccion';
  const tarifa = tarifaDeCalculoConRecibo(c, t.tarifaLeida, t.negocioAPdbt);
  if (tarifa) return aumentoSeSumaEn(tarifa) ? 'ia' : 'produccion';
  // nadie la sabe: el motor supone la de casa (casa) o PDBT (comercio), que lo suman, y GDMTO (industria), que no
  return c.tipo === 'industria' ? 'produccion' : 'supuesta';
}

/** La decisión que se guarda con el aumento (DecisionAumento): la tarifa con que calcularía el motor según la pantalla y los datos con
 *  que lo decidió. null = la pantalla no manda el aumento (modo 'produccion'). */
export function decisionAumento(
  c: Pick<Cuestionario, 'tipo' | 'consumo' | 'ampliacion' | 'dos_medidores' | 'tarifa'>,
  t: { tarifaLeida: TipoTarifa | null; negocioAPdbt: boolean },
): DecisionAumento | null {
  if (modoAumento(c, t) === 'produccion') return null;
  return { tarifa_leida: t.tarifaLeida, tarifa_cuestionario: c.tarifa, negocio_a_pdbt: t.negocioAPdbt, tarifa_calculo: tarifaDeCalculoConRecibo(c, t.tarifaLeida, t.negocioAPdbt) };
}

/** La tarifa con que calcula el motor a un cliente CON recibo (periodos.ts → tarifaDe + cambioDeTarifa), para decidirlo antes de
 *  calcular. null = ni el recibo ni el cuestionario la dicen (el motor la supone y lo avisa en rojo). */
export function tarifaDeCalculoConRecibo(c: Pick<Cuestionario, 'tipo' | 'tarifa' | 'dos_medidores'>, leida: TipoTarifa | null, negocioAPdbt: boolean): TipoTarifa | null {
  const cambio = cambioDeTarifa(c, leida, negocioAPdbt);
  if (cambio) return cambio.a;
  return leida ?? (c.tarifa === 'no_se' ? null : c.tarifa);
}

/** RECORTE DE PUNTA (peak shaving) con baterías grandes en GDMTH (ADENDA z54-1 y z55-2 de Diego, 28-oct-2026): solo en NICHOS,
 *  la industria que trabaja de noche (24 h o 2–3 turnos) y (a) no tiene techo ni terreno para paneles o (b) ya tiene paneles. En
 *  GDMTH van SIEMPRE primero los paneles: «le caben paneles» no se puede guardar (la pantalla no deja seguir) y «no trabaja de 6 a
 *  10 de la noche» tampoco (la batería no tendría qué recortar). Va con tipo «industria» (reusa giro, régimen, tensión del
 *  transformador, recibos GDMTH y CRM). Ausente/null = no es recorte de punta (todas las de antes). */
export const SIN_PANELES_RECORTE = ['sin_techo', 'ya_tiene'] as const;
export type SinPanelesRecorte = (typeof SIN_PANELES_RECORTE)[number];
export const ETIQUETA_SIN_PANELES_RECORTE: Record<SinPanelesRecorte, string> = {
  sin_techo: 'No tiene techo ni terreno para paneles',
  ya_tiene: 'Ya tiene paneles y no le caben más (sus recibos ya los traen)',
};
export const RecortePuntaCuestionario = z.strictObject({
  /** P1: «¿Trabaja entre semana de 6 a 10 de la noche? (24 h, o 2 o 3 turnos)». Solo «sí» se guarda. */
  trabaja_de_noche: z.literal(true),
  /** P2: «¿Por qué sin paneles nuevos?». «Le caben paneles» no se guarda: en GDMTH van primero los paneles (z54-1). */
  sin_paneles: z.enum(SIN_PANELES_RECORTE),
});
export type RecortePuntaCuestionario = z.infer<typeof RecortePuntaCuestionario>;
export const MENSAJE_RECORTE_SIN_NOCHE = 'La punta de CFE es de 6 a 10 de la noche (de 8 a 10 en verano): si no trabaja a esa hora, la batería no tiene qué recortar. Cotiza «Solo paneles».';
/** El recorte SÍ pide los 12 recibos (la punta de CADA mes sale de su recibo; ninguno se estima): lo dicen el paso «Situación», la
 *  pestaña Recibos y el amarillo de recibos que faltan (verificación de hoy70: los tres decían «no es obligatorio»). */
export const MENSAJE_RECORTE_PIDE_12 = 'El recorte de punta pide los 12 recibos GDMTH, uno por mes: la punta de cada mes sale de su recibo y ninguno se estima.';
export const MENSAJE_RECORTE_LE_CABEN = 'En GDMTH van primero los paneles: bajan la capacidad con la fórmula de CFE y se pagan antes que la batería. Cotiza «Solo paneles» (o la ampliación, si ya tiene).';

export const Cuestionario = z
  .strictObject({
    cliente: ClienteCuestionario,
    titular: Titular,
    tipo: TipoProyecto,
    consumo: z.strictObject({
      /** Casa o negocio nuevo: no hay recibos, el consumo se estima. */
      es_nuevo: z.boolean(),
      cambio: z.enum(['igual', 'aumenta', 'disminuye']),
      /** "Va a comprar un auto eléctrico", "cierra una sucursal"... lo lee la IA. */
      cambio_detalle: z.string().trim().max(500).nullable(),
      /** Si el cliente sabe cuánto consumirá al año (kWh). Desde z52 solo lo traen las de antes (la pantalla ofrece el aumento con IA). */
      consumo_futuro_kwh_anual: z.number().positive().nullable(),
      /** ADENDA z52-2: el aumento que aceptó el vendedor (ausente o null = ninguno; las de antes no lo traen). */
      aumento: AumentoConsumo.nullable().optional(),
    }),
    estimacion: Estimacion.nullable(),
    /** true = visita técnica hecha → cotización final; false → precotización. */
    visita_tecnica_hecha: z.boolean(),
    /** Ya tiene paneles y quiere más. */
    ampliacion: z.strictObject({ es_ampliacion: z.boolean(), existente: SistemaExistente.nullable() }),
    facturacion: z.enum(['mensual', 'bimestral', 'no_se']),
    dos_medidores: z.boolean(),
    tarifa: z.union([Tarifa, z.literal('no_se')]),
    /** Caja de texto libre que lee la IA con todo el contexto. */
    notas_libres: z.string().max(4_000),
    /** Contrato v2 · baterías. Opcional (la interfaz v1 no lo manda); solo con tipo "baterias" o "aislado". */
    respaldo: RespaldoCuestionario.nullable().optional(),
    /** Diego, 12-oct: régimen fiscal del cliente (negocio). Opcional EN EL ESQUEMA para que los borradores de antes sigan
     *  abriendo (null = no se preguntó: la propuesta de negocio dice la deducción sin cifras y el motor avisa en amarillo).
     *  Revisión de ventas, propuesta 5: en comercio e industria es OBLIGATORIO al crear (CuerpoCrearCotizacion) y una edición no
     *  lo puede quitar (servidor); para no saberlo está «No sé todavía». */
    fiscal: FiscalCuestionario.nullable().optional(),
    /** Diego, 12-oct: tipo de estructura. Opcional: ausente = losa/lámina (borradores de antes). */
    estructura: z.enum(TIPOS_ESTRUCTURA).nullable().optional(),
    /** Diego, 12-oct: fases y tensión del servicio de CFE. Opcional: ausente/null = no se preguntó (el motor lo deduce). */
    servicio: ServicioCuestionario.nullable().optional(),
    /** ADENDA z54/z55-2 (29-oct-2026): recorte de punta con baterías grandes (ver RecortePuntaCuestionario). Ausente/null = no. */
    recorte_punta: RecortePuntaCuestionario.nullable().optional(),
  })
  .superRefine((c, ctx) => {
    if (c.servicio && c.servicio.tension !== null && !TENSIONES_POR_FASES[c.servicio.fases].includes(c.servicio.tension))
      ctx.addIssue({
        code: 'custom',
        path: ['servicio', 'tension'],
        message: `Un servicio ${c.servicio.fases === 'monofasico' ? 'monofásico' : c.servicio.fases === 'bifasico' ? 'bifásico' : 'trifásico'} no viene en ${c.servicio.tension} V: elige ${TENSIONES_POR_FASES[c.servicio.fases].map((t) => `${t} V`).join(' o ')}.`,
      });
    if (c.respaldo && c.tipo !== 'baterias' && c.tipo !== 'aislado')
      ctx.addIssue({ code: 'custom', path: ['respaldo'], message: 'Lo que se respalda solo va con baterías o sistema aislado.' });
    if (c.respaldo && c.respaldo.tipo === 'aislado' && c.respaldo.cargas.length === 0)
      ctx.addIssue({ code: 'custom', path: ['respaldo', 'cargas'], message: 'Sistema aislado: agrega los equipos que van a funcionar (con sus watts).' });
    if (c.respaldo && (c.tipo === 'aislado') !== (c.respaldo.tipo === 'aislado'))
      ctx.addIssue({ code: 'custom', path: ['respaldo', 'tipo'], message: 'Sistema aislado (sin CFE) va con respaldo de tipo "aislado", y solo él.' });
    if (!c.titular.es_el_cliente && !c.titular.nombre)
      ctx.addIssue({ code: 'custom', path: ['titular', 'nombre'], message: 'Si el recibo está a nombre de otra persona, escribe su nombre.' });
    if (c.consumo.es_nuevo && !c.estimacion)
      ctx.addIssue({ code: 'custom', path: ['estimacion'], message: 'Cliente nuevo: hace falta cómo estimar su consumo.' });
    // Sin esto el borrador se crea y el motor truena después ("la estimación no trae equipos ni kWh al mes").
    if (c.estimacion && c.estimacion.metodo === 'cuadro_cargas' && c.estimacion.cargas.length === 0)
      ctx.addIssue({ code: 'custom', path: ['estimacion', 'cargas'], message: 'Cuadro de cargas: agrega al menos un equipo.' });
    if (c.estimacion && c.estimacion.metodo === 'pago' && !c.estimacion.pago)
      ctx.addIssue({ code: 'custom', path: ['estimacion', 'pago'], message: 'Escribe cuánto paga a CFE (al bimestre o al mes).' });
    if (c.estimacion && c.estimacion.metodo !== 'cuadro_cargas' && c.estimacion.metodo !== 'pago' && c.estimacion.kwh_mes === null)
      ctx.addIssue({ code: 'custom', path: ['estimacion', 'kwh_mes'], message: 'Escribe el consumo estimado en kWh al mes.' });
    if (!c.consumo.es_nuevo && c.estimacion)
      ctx.addIssue({ code: 'custom', path: ['estimacion'], message: 'La estimación solo va con clientes nuevos (sin recibos).' });
    if (c.consumo.cambio === 'igual' && c.consumo.consumo_futuro_kwh_anual !== null)
      ctx.addIssue({ code: 'custom', path: ['consumo', 'consumo_futuro_kwh_anual'], message: 'Si el consumo no cambia, no va consumo futuro.' });
    const au = c.consumo.aumento;
    if (au) {
      const porque = c.consumo.cambio !== 'aumenta'
        ? 'el cuestionario no dice que el consumo va a subir'
        : c.consumo.es_nuevo
          ? 'es cliente nuevo (sin recibo no hay consumo de hoy al que sumarlo)'
          : c.ampliacion.es_ampliacion
            ? 'es una ampliación (todavía no se combina con el aumento)'
            : c.tipo !== 'casa' && c.tipo !== 'comercio' && c.tipo !== 'industria'
              ? `el tipo ${c.tipo} no lo usa`
              : c.dos_medidores
                ? 'con dos medidores todavía no se reparte'
                : c.consumo.consumo_futuro_kwh_anual !== null
                  ? 'ya trae el consumo futuro de antes (usa uno de los dos)'
                  : !(au.kwh_mes.reduce((a, b) => a + b, 0) > 0)
                    ? 'suma 0 kWh'
                    : null;
      if (porque) ctx.addIssue({ code: 'custom', path: ['consumo', 'aumento'], message: `El aumento de consumo no va aquí: ${porque}.` });
      // Bloqueo del verificador (29-oct): la decisión guardada tiene que ser una que la pantalla toma (DecisionAumento): nunca con
      // una tarifa que no suma el aumento, y «supuesta» (sin tarifa) solo sin recibo y con «No sé».
      const d = au.pantalla;
      const malDecidido = d.tarifa_calculo !== null && !aumentoSeSumaEn(d.tarifa_calculo)
        ? `la pantalla no ofrece el aumento con IA en ${d.tarifa_calculo} (ahí va el consumo futuro)`
        : d.tarifa_calculo === null && (d.tarifa_leida !== null || d.tarifa_cuestionario !== 'no_se')
          ? 'la tarifa «supuesta» solo va sin recibo y con «No sé» en el cuestionario'
          : null;
      if (malDecidido) ctx.addIssue({ code: 'custom', path: ['consumo', 'aumento', 'pantalla'], message: `El aumento de consumo no va aquí: ${malDecidido}.` });
    }
    if (c.ampliacion.es_ampliacion && !c.ampliacion.existente)
      ctx.addIssue({ code: 'custom', path: ['ampliacion', 'existente'], message: 'Ampliación: describe el sistema que ya tiene.' });
    const ex = c.ampliacion.existente;
    if (ex?.conexion === 'inversor_actual' && !ex.inversores?.length)
      ctx.addIssue({ code: 'custom', path: ['ampliacion', 'existente', 'inversores'], message: 'Si los paneles nuevos van a su inversor actual, escribe cuál es (marca, kW y cantidad).' });
    if (ex && ex.paneles !== null && ex.panel_w !== null && ex.kwp !== null && Math.abs((ex.paneles * ex.panel_w) / 1000 - ex.kwp) >= 0.01)
      ctx.addIssue({ code: 'custom', path: ['ampliacion', 'existente', 'kwp'], message: `El sistema que ya tiene no cuadra: ${ex.paneles} paneles × ${ex.panel_w} W = ${((ex.paneles * ex.panel_w) / 1000).toFixed(2)} kWp.` });
    if ((c.tipo === 'comercio' || c.tipo === 'industria') && !c.cliente.giro)
      ctx.addIssue({ code: 'custom', path: ['cliente', 'giro'], message: 'Negocio: escribe el giro.' });
    // Ampliación de baterías (27-oct-2026): no pasa por el motor; del cuestionario solo valen el cliente, la etapa y las notas.
    if (c.tipo === 'ampliacion_baterias') {
      if (c.consumo.es_nuevo || c.estimacion || c.ampliacion.es_ampliacion || c.dos_medidores || c.respaldo)
        ctx.addIssue({ code: 'custom', path: ['tipo'], message: 'Ampliación de baterías: no lleva consumo nuevo, estimación, ampliación de paneles, dos medidores ni respaldo (su captura va en la pantalla de la ampliación).' });
    }
    // Recorte de punta (z54): solo industria con un recibo GDMTH de CFE, un medidor y sus recibos (no cliente nuevo ni ampliación).
    if (c.recorte_punta) {
      if (c.tipo !== 'industria')
        ctx.addIssue({ code: 'custom', path: ['recorte_punta'], message: 'El recorte de punta es solo para industria (ADENDA z54: industria que trabaja de noche).' });
      if (c.tarifa !== 'GDMTH' && c.tarifa !== 'no_se')
        ctx.addIssue({ code: 'custom', path: ['tarifa'], message: `El recorte de punta es solo para tarifa GDMTH (la de punta de noche); el cuestionario dice ${c.tarifa}.` });
      if (c.dos_medidores)
        ctx.addIssue({ code: 'custom', path: ['dos_medidores'], message: 'Recorte de punta: un medidor, una obra (cotiza cada servicio por separado).' });
      if (c.consumo.es_nuevo || c.estimacion)
        ctx.addIssue({ code: 'custom', path: ['consumo', 'es_nuevo'], message: 'Recorte de punta: se calcula con los 12 recibos GDMTH del cliente (la punta de cada mes); un cliente nuevo sin recibos no se cotiza.' });
      if (c.ampliacion.es_ampliacion)
        ctx.addIssue({ code: 'custom', path: ['ampliacion'], message: 'Recorte de punta: es solo la batería (no lleva paneles nuevos). Si ya tiene paneles, elige «Ya tiene paneles» en el recorte.' });
      if (c.respaldo)
        ctx.addIssue({ code: 'custom', path: ['respaldo'], message: 'Recorte de punta: no lleva respaldo de baterías para apagones.' });
    }
    if (c.cliente.tipo && c.tipo !== 'baterias' && c.tipo !== 'aislado' && c.tipo !== 'ampliacion_baterias' && c.cliente.tipo !== c.tipo)
      ctx.addIssue({ code: 'custom', path: ['cliente', 'tipo'], message: `El tipo de cliente (${c.cliente.tipo}) no coincide con el de la cotización (${c.tipo}).` });
  });
export type Cuestionario = z.infer<typeof Cuestionario>;

/** Etapa que sale del cuestionario. */
export function etapaDe(c: Pick<Cuestionario, 'visita_tecnica_hecha'>): Etapa {
  return c.visita_tecnica_hecha ? 'final' : 'precotizacion';
}

/** Recibos que se SUGIEREN por medidor (ninguno es obligatorio: Diego, junta del 13-oct). Un recibo doméstico, PDBT o
 *  GDMTO trae el historial del año y basta; en GDMTH el historial no trae el reparto base/intermedia/punta ni los importes:
 *  se piden hasta 12, y con los que haya se cotiza (lo que falta se estima, con aviso amarillo). Cliente nuevo: 0. */
export function recibosEsperados(c: Pick<Cuestionario, 'tarifa' | 'consumo'>, tarifaLeida: TipoTarifa | null = null): number {
  if (c.consumo.es_nuevo) return 0;
  const t = tarifaLeida ?? (c.tarifa === 'no_se' ? null : c.tarifa);
  if (t && (TARIFAS_QUE_PIDEN_12_RECIBOS as readonly string[]).includes(t)) return 12;
  return 1;
}

export function esMediaTension(t: TipoTarifa | 'no_se' | null): boolean {
  return t !== null && t !== 'no_se' && TARIFAS_MEDIA_TENSION.includes(t);
}

/** Formato de la familia que le toca a la cotización, por la TARIFA (contrato final de la familia, 10-oct-2026), no
 *  solo por el tipo del cuestionario. `tarifaCalculada` = la del resultado del motor (la del recibo leído); sin ella, la
 *  del cuestionario.
 *   - casa (1, 1A–1F, DAC) → residencial;
 *   - negocio PDBT o GDBT → comercial (también "industria" con esas tarifas: el industrial es solo media tensión);
 *   - GDMTO → comercial si es comercio; industrial si es industria (planta, 12 recibos mensuales con cargos);
 *   - GDMTH (con paneles) → industrial, sea comercio o industria (el comercial no acepta GDMTH: N8 de la plantilla);
 *   - baterías (respaldo o híbrido) y aislado → baterias.
 *   - recorte de punta (cuestionario.recorte_punta, z54, 29-oct-2026) → horaria (baterías para recortar la punta en GDMTH).
 *  "desglose" y "multisitio" no salen del cuestionario. Negocio sin tarifa conocida: comercial (comercio) o industrial (industria). */
export function formatoPara(c: Pick<Cuestionario, 'tipo' | 'tarifa'> & { recorte_punta?: RecortePuntaCuestionario | null | undefined }, tarifaCalculada: TipoTarifa | null = null): FormatoFamilia {
  if (c.tipo === 'ampliacion_baterias') return 'piezas';
  // Recorte de punta (z54, 29-oct-2026): la batería grande sola va en el formato horaria (5 hojas + anexo, ESPEC 3.5).
  if (c.recorte_punta) return 'horaria';
  if (c.tipo === 'baterias' || c.tipo === 'aislado') return 'baterias';
  if (c.tipo === 'casa') return 'residencial';
  const t = tarifaCalculada ?? (c.tarifa === 'no_se' ? null : c.tarifa);
  if (t === 'GDMTH') return 'industrial';
  if (t === 'GDMTO') return c.tipo === 'industria' ? 'industrial' : 'comercial';
  if (t === 'PDBT' || t === 'GDBT') return 'comercial';
  return c.tipo === 'industria' ? 'industrial' : 'comercial';
}

/** Lo que el generador de la familia TODAVÍA NO imprime por el TIPO de la cotización. La rama de «todo se elige desde el
 *  inicio» avisaba aquí que el híbrido y el aislado no sacaban PDF; con la de baterías (integración del 14-oct) los dos ya
 *  imprimen (paquetes/pdf → mapearBaterias: hibridoV1 y aisladoV1), y desde el 28-oct-2026 (ADENDA z48-3) también el híbrido de un
 *  cliente NUEVO, con consumo estimado como la casa nueva: hoy ningún tipo se detiene por serlo. Se queda como
 *  el ÚNICO lugar donde se diría (tarjeta de «Qué cotizas», «Revisa antes de crear», pestaña PDF y servidor simulado lo leen)
 *  si algún tipo nuevo (p. ej. peak shaving) entra antes de que la plantilla lo imprima. (null = sí sale, si lo demás está bien). */
export function pdfTodaviaNoSale(c: {
  tipo: TipoProyecto | null;
  respaldo?: { tipo: RespaldoCuestionario['tipo'] } | null | undefined;
  consumo?: { es_nuevo: boolean } | null | undefined;
}): string | null {
  // ADENDA z48-3 (Diego, 28-oct-2026): el híbrido de un cliente NUEVO ya no se frena aquí (antes era el único, y el vendedor lo
  // cotizaba como «Paneles», sin baterías): sale con consumo estimado (variante consumo_proyectado de la plantilla -6n).
  void c;
  return null;
}

/** Formato por el TIPO solamente (antes de la etapa "Conectar"). Para elegir el PDF usa `formatoPara` (por la tarifa):
 *  "comercio" con GDMTH aquí da comercial, y el comercial truena con GDMTH. Aislado va en el formato de baterías. */
export function formatoDe(tipo: Cuestionario['tipo']): FormatoFamilia {
  switch (tipo) {
    case 'casa':
      return 'residencial';
    case 'comercio':
      return 'comercial';
    case 'industria':
      return 'industrial';
    case 'baterias':
    case 'aislado':
      return 'baterias';
    case 'ampliacion_baterias':
      return 'piezas';
  }
}

/** z34-6 (Diego, 26-oct-2026): los paneles que el cliente YA tiene en una ampliación (0 si no es ampliación o no se sabe). Si no
 *  capturaron cuántos, salen de sus kWp ÷ W del panel. «Listo para ampliar/crecer hasta N paneles» cuenta el TOTAL: estos + los
 *  nuevos de la Exacta. */
export function panelesQueYaTiene(c: Pick<Cuestionario, 'ampliacion'>): number {
  const ex = c.ampliacion.es_ampliacion ? c.ampliacion.existente : null;
  if (!ex) return 0;
  if (ex.paneles) return ex.paneles;
  return ex.kwp && ex.panel_w ? Math.round((ex.kwp * 1000) / ex.panel_w) : 0;
}

/** El cuestionario de una ampliación de baterías: el cliente, si ya se hizo la visita técnica (etapa) y las notas. Lo demás
 *  (consumo, tarifa, medidores) no aplica: no hay recibo ni motor solar. Lo usan la pantalla, el servidor simulado y las pruebas. */
export function cuestionarioAmpliacion(cliente: ClienteCuestionario, o: { visita_tecnica_hecha: boolean; notas_libres?: string }): Cuestionario {
  return {
    cliente,
    titular: { es_el_cliente: true, nombre: null },
    tipo: 'ampliacion_baterias',
    consumo: { es_nuevo: false, cambio: 'igual', cambio_detalle: null, consumo_futuro_kwh_anual: null },
    estimacion: null,
    visita_tecnica_hecha: o.visita_tecnica_hecha,
    ampliacion: { es_ampliacion: false, existente: null },
    facturacion: 'no_se',
    dos_medidores: false,
    tarifa: 'no_se',
    notas_libres: o.notas_libres ?? '',
    respaldo: null,
  };
}

